import React, { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";

import { researchPosts } from "../constants/researchPosts";
import { useResearchPostDetail } from "../hooks/useResearchPostDetail";

const FALLBACK_IMAGE = "/images/initiatives/Picture1.png";

const ResearchDetail: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const { post: apiPost, loading, error } = useResearchPostDetail(slug);

  useEffect(() => {
    document.body.classList.add("page-research-details");
    return () => {
      document.body.classList.remove("page-research-details");
    };
  }, []);

  const fallbackPost = useMemo(() => {
    if (!slug) {
      return null;
    }
    return researchPosts.find((p) => p.slug === slug);
  }, [slug]);

  const detailPost = useMemo(() => {
    if (apiPost) {
      const tags = Array.isArray(apiPost.eng_tags)
        ? apiPost.eng_tags
            .map((tag) => tag?.tag_name_eng)
            .filter((tagName): tagName is string => Boolean(tagName))
        : [];

      return {
        title: apiPost.eng_title || "Research Details",
        heroImage: apiPost.eng_thumbnail || FALLBACK_IMAGE,
        description:
          apiPost.eng_description || apiPost.eng_caption || "No description.",
        content:
          apiPost.eng_content ||
          apiPost.eng_description ||
          apiPost.eng_caption ||
          "",
        tags,
        downloadLink: apiPost.download_file || "#",
      };
    }

    if (fallbackPost) {
      return {
        title: fallbackPost.title,
        heroImage: fallbackPost.image,
        description: fallbackPost.excerpt,
        content: `<p>${fallbackPost.excerpt}</p>`,
        tags: fallbackPost.tags,
        downloadLink: fallbackPost.downloadLink,
      };
    }

    return null;
  }, [apiPost, fallbackPost]);

  const pageBgStyle = useMemo(
    () => ({
      backgroundImage: detailPost
        ? `url('${detailPost.heroImage}')`
        : undefined,
      backgroundSize: "cover",
      backgroundPosition: "center",
      paddingTop: "140px",
      paddingBottom: "80px",
      color: "white",
      textAlign: "center" as const,
      position: "relative" as const,
      overflow: "hidden" as const,
    }),
    [detailPost]
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading research...</span>
          </div>
        </div>
      );
    }

    if (error && !detailPost) {
      return <p>Failed to load research details. Please try again later.</p>;
    }

    if (!detailPost) {
      return <p>We could not find the requested research post.</p>;
    }

    return (
      <>
        {detailPost.tags.length > 0 && (
          <div className="blog-details__tags d-flex gap-2 align-items-center flex-wrap mb-4">
            {detailPost.tags.map((tag, index) => (
              <Link key={index} className="tag" to={`/tag/${encodeURIComponent(tag)}`}>
                {tag}
              </Link>
            ))}
          </div>
        )}
        {detailPost.content ? (
          <div
            className="blog-details__text"
            dangerouslySetInnerHTML={{ __html: detailPost.content }}
          />
        ) : (
          <p>{detailPost.description}</p>
        )}
      </>
    );
  };

  return (
    <main id="main-content" role="main">
      <section className="page-title" style={pageBgStyle}>
        <div
          className="page-title__overlay"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(135deg, rgba(0, 51, 102, 0.8) 0%, rgba(0, 102, 204, 0.8) 100%)",
            zIndex: 1,
          }}
        ></div>
        <div className="container" style={{ position: "relative", zIndex: 2 }}>
          <div className="page-title__inner">
            <div className="page-title__title-box">
              <h1 className="page-title__title" style={{ color: "#ffffff" }}>
                {detailPost ? detailPost.title : ""}
              </h1>
            </div>
            <p className="page-title__text">{detailPost?.description || ""}</p>
          </div>
        </div>
      </section>

      <section className="blog-details pt-5 pb-5 bg-white">
        <div className="container">
          <div
            className="blog-details__content mx-auto"
            style={{
              maxWidth: "830px",
              background: "#fff",
              borderRadius: "2rem",
              boxShadow: "0 6px 36px -10px rgba(31,55,100,0.08)",
              padding: "3rem 2rem 2.5rem 2rem",
            }}
          >
            <div className="mb-4">
              <Link
                to="/research"
                className="btn-main btn-line mb-3"
                // style={{ fontSize: ".97rem", display: "inline-block" }}
              >
                <i className="fas fa-arrow-left me-2"></i> Back to Research
              </Link>
            </div>
            <div className="blog-details__text mb-4">
              <h2>{detailPost?.title}</h2>
              {renderContent()}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ResearchDetail;
